import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import {
    faThumbsDown as faThumbsDownSolid,
    faThumbsUp as faThumbsUpSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RobotSVG from 'assets/Robot.svg';
import { FeedbackModal, FeedbackType } from 'components/chatbot/FeedbackModal';
import {
    BotBubble,
    BotIcon,
    BotMessage,
    BottomDiv,
    FeedbackButton,
    HoverTimestamp,
    MessageTimestamp,
    TypingBubble,
    UserBubble,
    UserIcon,
    UserMessage,
} from 'components/chatbot/styled';
import { MessageInfo } from 'components/chatbot/types';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { differenceInDays, differenceInSeconds } from 'date-fns';
import { Fragment, useState } from 'react';

type FeedbackModalInfo = Readonly<{
    feedbackType: FeedbackType;
    messageIndex: number;
}>;

function escapeCharacter(str: string): JSX.Element {
    const splitStr = str.split('\\n');
    return (
        <>
            {splitStr.map((line, index) => (
                <Fragment key={line}>
                    {line.replace(/\\"/g, '"')}
                    {index !== splitStr.length - 1 && <br />}
                </Fragment>
            ))}
        </>
    );
}

const formatTimestamp = (timestamp: number | undefined, hover: boolean) => {
    if (!timestamp) {
        return '';
    }
    const now = new Date();
    const timestampDate = new Date(timestamp);
    const diffInSeconds = differenceInSeconds(now, timestampDate);
    const diffInDays = differenceInDays(now, timestampDate);

    if (diffInDays === 0) {
        if (diffInSeconds < 60 && !hover) {
            return 'Just now';
        }
        if (diffInSeconds < 3600 && !hover) {
            if (Math.floor(diffInSeconds / 60) === 1) {
                return `${Math.floor(diffInSeconds / 60)} minute ago`;
            }
            return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        }
        return timestampDate.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
        });
    }
    if (diffInDays === 1 && !hover) {
        return `Yesterday ${timestampDate.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
        })}`;
    }
    if (hover) {
        return `${timestampDate.toLocaleDateString([], {
            month: 'numeric',
            day: 'numeric',
        })} ${timestampDate.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
        })}`;
    }
    return `${timestampDate.toLocaleDateString([], {
        month: 'long',
        day: '2-digit',
    })} ${timestampDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

export type MessagesProps = Readonly<{
    messages: MessageInfo[];
    onFeedback: (message: MessageInfo, feedback: FeedbackType) => void;
    loading: boolean;
}>;

export function Messages({ messages, loading, onFeedback }: MessagesProps) {
    const { data: currentUser } = useCurrentUserInfo();
    const [messageHover, setMessageHover] = useState(-1);
    const [feedbackModalInfo, setFeedbackModalInfo] = useState<FeedbackModalInfo>();

    const userAbbreviation = currentUser
        ? `${currentUser.FirstName.charAt(0)}${currentUser.LastName.charAt(0)}`
        : 'UN';

    return (
        <div>
            {messages.map((message, index) => (
                <div
                    onMouseEnter={() => setMessageHover(index)}
                    onMouseLeave={() => setMessageHover(-1)}
                    key={message.id}
                >
                    {message.role === 'assistant' ? (
                        <BotBubble>
                            <BotIcon>
                                <img
                                    src={RobotSVG}
                                    alt="KCFchat Icon"
                                    style={{
                                        height: '2.8rem',
                                        width: '2.8rem',
                                        justifyContent: 'center',
                                    }}
                                />
                            </BotIcon>
                            <BotMessage>{escapeCharacter(message.content)}</BotMessage>
                            {message.isEligible && (
                                <div style={{ marginLeft: '64px', marginTop: '-8px' }}>
                                    {message.feedback === 'positive' && (
                                        <FeedbackButton disabled>
                                            <FontAwesomeIcon
                                                icon={faThumbsUpSolid}
                                                size="xl"
                                            />
                                        </FeedbackButton>
                                    )}
                                    {message.feedback === 'negative' && (
                                        <FeedbackButton disabled>
                                            <FontAwesomeIcon
                                                icon={faThumbsDownSolid}
                                                size="xl"
                                                flip="horizontal"
                                            />
                                        </FeedbackButton>
                                    )}
                                    {!message.feedback && (
                                        <>
                                            <FeedbackButton
                                                onClick={() =>
                                                    setFeedbackModalInfo({
                                                        feedbackType: 'positive',
                                                        messageIndex: index,
                                                    })
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faThumbsUp}
                                                    size="xl"
                                                />
                                            </FeedbackButton>
                                            <FeedbackButton
                                                onClick={() =>
                                                    setFeedbackModalInfo({
                                                        feedbackType: 'negative',
                                                        messageIndex: index,
                                                    })
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faThumbsDown}
                                                    size="xl"
                                                    flip="horizontal"
                                                />
                                            </FeedbackButton>
                                        </>
                                    )}
                                </div>
                            )}
                        </BotBubble>
                    ) : (
                        <UserBubble>
                            <UserIcon>{userAbbreviation}</UserIcon>
                            <UserMessage>{message.content}</UserMessage>
                        </UserBubble>
                    )}
                    {messageHover === index && (
                        <HoverTimestamp>
                            {formatTimestamp(message.timestamp, true)}
                        </HoverTimestamp>
                    )}
                </div>
            ))}
            <MessageTimestamp>
                {formatTimestamp(messages[messages.length - 1]?.timestamp, false)}
            </MessageTimestamp>
            {/* Typing bubble animation */}
            {loading && (
                <div>
                    <BotBubble>
                        <BotIcon>
                            <img
                                src={RobotSVG}
                                alt="KCFchat Icon"
                                style={{
                                    height: '2.8rem',
                                    width: '2.8rem',
                                    justifyContent: 'center',
                                }}
                            />
                        </BotIcon>
                        <TypingBubble
                            style={{ marginRight: '-1rem', animationDelay: '0s' }}
                        />
                        <TypingBubble
                            style={{ marginRight: '-1rem', animationDelay: '0.5s' }}
                        />
                        <TypingBubble />
                    </BotBubble>
                    <BottomDiv />
                </div>
            )}
            {feedbackModalInfo && (
                <FeedbackModal
                    feedbackType={feedbackModalInfo.feedbackType}
                    conversation={messages}
                    onSuccess={() => {
                        onFeedback(
                            messages[feedbackModalInfo.messageIndex],
                            feedbackModalInfo.feedbackType,
                        );
                        setFeedbackModalInfo(undefined);
                    }}
                    onCancel={() => setFeedbackModalInfo(undefined)}
                />
            )}
        </div>
    );
}
